/**
 * Raport zarządu — teksty widoku zakładki „Klienci” (`mbr.customers.ui.*`): most zamawiających, ranking handlowców,
 * porównanie ustawień z Excelem, lista z filtrami i plakietkami AI, siatka sezonów. WŁAŚCICIEL: agent sekcji customers.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrCustomersUiPl = {
  bridge: {
    title: 'Skąd zmiana liczby zamawiających',
    subtitle: '{previous} → {current}. Kliknij słupek, aby zobaczyć tych klientów na liście.',
    steps: {
      previous: 'Start\n{period}',
      new: 'Nowi',
      recovered: 'Odzyskani',
      returned: 'Wrócili po\nprzerwie',
      stopped: 'Przestali\nzamawiać',
      current: 'Koniec\n{period}',
    },
    tooltip: {
      previous: 'Zamawiający w {period}: {value}',
      current: 'Zamawiający w {period}: {value}',
      new: 'Nowi (pierwsze zamówienie): {value}',
      recovered: 'Odzyskani po co najmniej {months} mies. przerwy: {value}',
      returned: 'Wrócili po krótszej przerwie albo po sezonie: {value}',
      stopped: 'Zamawiali w {period}, w tym miesiącu nie: {value} (w tym utraceni: {lost})',
    },
    legendTotal: 'zamawiający',
    legendUp: 'przybyło',
    legendDown: 'ubyło',
    axisNote: 'Oś zaczyna się od {min} klientów, żeby zmiany były widoczne.',
    excel:
      'Wzór z Excela: {previous} + {gained} pozyskanych − {lost} utraconych = {formula} (różnica {diff} to klienci po krótkiej przerwie i ci, którzy przestali zamawiać, ale jeszcze nie są utraceni).',
  },
  modes: {
    subtitle: 'Ten sam miesiąc liczony wg ustawień raportu i tak jak w Excelu klienta (utrata po 1 mies.).',
    measure: 'Miara',
    difference: 'Różnica',
    open: 'Pokaż klientów',
  },
  reps: {
    subtitle: 'Pozyskani minus utraceni wg obecnego opiekuna. Kliknij handlowca, aby zobaczyć jego klientów.',
    rank: 'Miejsce',
    legendPositive: 'bilans dodatni',
    legendNegative: 'bilans ujemny',
    tooltip: '{name}: pozyskani {gainedCount} ({gained}), utraceni {lostCount} ({lost}), bilans {balance}',
    ai: 'Wniosek AI dotyczy tego portfela',
  },
  flows: {
    title: 'Statusy klientów w miesiącu',
    subtitle: 'Liczba, obrót i zysk brutto wg statusu. Kliknij wiersz, aby zobaczyć listę.',
    partial: 'część klientów bez kosztu towaru',
  },
  list: {
    subtitle: 'Kliknij nazwę, aby otworzyć kartę klienta. Klienci bez zamówienia mogą dostać zadanie „odzyskaj”.',
    filters: 'Status klientów',
    ai: 'Wskazani przez AI',
    focus: 'Wniosek AI: {label}',
    owner: 'Handlowiec: {name}',
    clear: 'Wyczyść filtr',
    selectRecoverable: 'Zaznacz wszystkich do odzyskania ({count})',
    bulkHint: 'Zaznacz klientów bez zamówienia — zadania trafią do ich opiekunów.',
    bulkSelected: 'Zaznaczono {count}: zadania „odzyskaj” trafią do opiekunów tych klientów.',
    rows: 'Klientów na liście: {count}',
    aiBadge: 'AI',
    noTasks: 'Tworzenie zadań wymaga uprawnienia do zadań.',
  },
  seasons: {
    legendPaused: 'bez zamówień (sezon)',
    legendActive: 'zamawiają',
    hint: 'Kliknij miesiąc, aby go włączyć lub wyłączyć, potem zapisz segment.',
    configured: '{configured} z {customers} z sezonem',
  },
};

export const mbrCustomersUiEn: Shape<typeof mbrCustomersUiPl> = {
  bridge: {
    title: 'Where the change in ordering customers comes from',
    subtitle: '{previous} → {current}. Click a bar to see these customers in the list.',
    steps: {
      previous: 'Start\n{period}',
      new: 'New',
      recovered: 'Recovered',
      returned: 'Back after\na break',
      stopped: 'Stopped\nordering',
      current: 'End\n{period}',
    },
    tooltip: {
      previous: 'Ordering in {period}: {value}',
      current: 'Ordering in {period}: {value}',
      new: 'New (first order): {value}',
      recovered: 'Recovered after at least {months} months: {value}',
      returned: 'Back after a shorter break or a season: {value}',
      stopped: 'Ordered in {period}, not this month: {value} (lost among them: {lost})',
    },
    legendTotal: 'ordering',
    legendUp: 'added',
    legendDown: 'removed',
    axisNote: 'The axis starts at {min} customers so the changes are visible.',
    excel:
      'Excel formula: {previous} + {gained} gained − {lost} lost = {formula} (the difference {diff} is customers back after a short break and those who stopped ordering but are not lost yet).',
  },
  modes: {
    subtitle: 'The same month by the report settings and as in the client’s Excel (lost after 1 month).',
    measure: 'Measure',
    difference: 'Difference',
    open: 'Show customers',
  },
  reps: {
    subtitle: 'Gained minus lost by the current owner. Click a rep to see their customers.',
    rank: 'Rank',
    legendPositive: 'positive balance',
    legendNegative: 'negative balance',
    tooltip: '{name}: gained {gainedCount} ({gained}), lost {lostCount} ({lost}), balance {balance}',
    ai: 'An AI insight is about this portfolio',
  },
  flows: {
    title: 'Customer statuses in the month',
    subtitle: 'Count, turnover and gross profit by status. Click a row to see the list.',
    partial: 'some customers without goods cost',
  },
  list: {
    subtitle: 'Click a name to open the customer card. Customers without an order can get a “recover” task.',
    filters: 'Customer status',
    ai: 'Flagged by AI',
    focus: 'AI insight: {label}',
    owner: 'Sales rep: {name}',
    clear: 'Clear filter',
    selectRecoverable: 'Select all to recover ({count})',
    bulkHint: 'Select customers without an order — tasks go to their owners.',
    bulkSelected: '{count} selected: “recover” tasks go to the owners of these customers.',
    rows: 'Customers in the list: {count}',
    aiBadge: 'AI',
    noTasks: 'Creating tasks requires the tasks permission.',
  },
  seasons: {
    legendPaused: 'no orders (season)',
    legendActive: 'ordering',
    hint: 'Click a month to switch it on or off, then save the segment.',
    configured: '{configured} of {customers} with a season',
  },
};
